import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Switch, Select,
  Space, Popconfirm, message, Card, Tag, DatePicker
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CalendarOutlined, DollarOutlined, ThunderboltOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getBranches } from '../../api/branches';
import BranchSelect from '../common/BranchSelect';
import {
  getTerms, createTerm, updateTerm, deleteTerm,
  getTermHolidays, addTermHoliday, deleteTermHoliday,
  getTermPrices, addTermPrice, updateTermPrice, deleteTermPrice,
} from '../../api/settings';
import useAuthStore from '../../store/authStore';
import { apiErrorMessage } from '../../utils/apiError';
import GenerateSessionsModal from './GenerateSessionsModal';

const { Option } = Select;
const { RangePicker } = DatePicker;

const TIME_OPTIONS = [];
for (let h = 6; h <= 23; h++) {
  for (const m of [0, 30]) {
    const label = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    TIME_OPTIONS.push({ label, value: label });
  }
}

const SUPER_ROLES = ['developer_admin', 'super_admin'];
const GENERATE_SESSIONS_ROLES = ['developer_admin', 'super_admin', 'branch_manager'];

const TermsTab = ({ branchId } = {}) => {
  const { user } = useAuthStore();
  const isSuperAdmin = SUPER_ROLES.includes(user?.role);
  // ROL **VEYA** İZİN — projenin yerleşik kuralı (frontend/CLAUDE.md:
  // "Yeni sayfa eklerken rol kapısına İZNİ de ekle").
  //
  // Varsayılanda ikisi zaten örtüşüyor: `terms.edit` süper admin ve şube
  // müdüründe var, düğme de o üç rolde görünüyordu. Açık olan tek durum
  // DEVREDİLEN yetkiydi: Yetkiler panelinden bir öğretmene `terms.edit`
  // verildiğinde uç açılıyor ama düğme çıkmıyordu — yönetici yetkiyi
  // verdiğini sanıyor, kişi giriş yolu bulamıyordu. (Aynı desen 07.08.2026'da
  // gerçek bir şikâyet olmuştu.)
  const canGenerateSessions = GENERATE_SESSIONS_ROLES.includes(user?.role)
    || (user?.permissions || []).includes('terms.edit');
  const [terms, setTerms] = useState([]);
  const [branches, setBranches] = useState([]);
  // Toplu ders üretimi artık form ile (GenerateSessionsModal): gün bazlı
  // saat, derslik seçimi ve önizleme gerekiyor.
  const [generateTerm, setGenerateTerm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const [branchFilter, setBranchFilter] = useState(branchId ?? null);

  // Tatil günleri
  const [holidayModalOpen, setHolidayModalOpen] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [holidayForm] = Form.useForm();
  const [confirmSessions, setConfirmSessions] = useState(null); // 409 onay state
  const [pendingHoliday, setPendingHoliday] = useState(null);   // onay bekleyen form değerleri

  // Fiyatlar
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [prices, setPrices] = useState([]);
  const [priceForm] = Form.useForm();
  const [editingPrice, setEditingPrice] = useState(null);

  const fetchTerms = async (page = 1) => {
    setLoading(true);
    try {
      const res = await getTerms({ page, per_page: 15, branch_id: branchFilter || undefined });
      setTerms(res.data.data);
      setPagination({
        current: res.data.current_page,
        pageSize: res.data.per_page,
        total: res.data.total,
      });
    } catch (error) {
      message.error('Dönemler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await getBranches({ per_page: 100 });
      setBranches(res.data.data);
    } catch (error) {
      console.error('Şubeler yüklenemedi.');
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchTerms(1);
  }, [branchFilter]);

  const handleSubmit = async (values) => {
    try {
      const data = {
        ...values,
        start_date: values.date_range[0].format('YYYY-MM-DD'),
        end_date: values.date_range[1].format('YYYY-MM-DD'),
        first_slot_time: values.first_slot_time ? values.first_slot_time.substring(0, 5) : null,
        last_slot_time:  values.last_slot_time  ? values.last_slot_time.substring(0, 5)  : null,
        ...(branchId ? { branch_id: branchId } : {}),
      };
      delete data.date_range;

      if (editingTerm) {
        await updateTerm(editingTerm.id, data);
        message.success('Dönem güncellendi.');
      } else {
        await createTerm(data);
        message.success('Dönem oluşturuldu.');
      }
      setModalOpen(false);
      form.resetFields();
      setEditingTerm(null);
      fetchTerms();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    }
  };

  const handleEdit = (term) => {
    setEditingTerm(term);
    form.setFieldsValue({
      ...term,
      date_range:      [dayjs(term.start_date), dayjs(term.end_date)],
      first_slot_time: term.first_slot_time || null,
      last_slot_time:  term.last_slot_time  || null,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteTerm(id);
      message.success('Dönem silindi.');
      fetchTerms();
    } catch (error) {
      message.error('Dönem silinemedi.');
    }
  };

  const handleHolidays = async (term) => {
    setSelectedTerm(term);
    try {
      const res = await getTermHolidays(term.id);
      setHolidays(res.data);
    } catch (error) {
      message.error('Tatil günleri yüklenemedi.');
    }
    setHolidayModalOpen(true);
  };

  const handleAddHoliday = async (values, confirm = false) => {
    try {
      await addTermHoliday(selectedTerm.id, {
        date: values.date.format('YYYY-MM-DD'),
        description: values.description,
        confirm,
      });
      message.success('Tatil günü eklendi.');
      holidayForm.resetFields();
      setConfirmSessions(null);
      setPendingHoliday(null);
      const res = await getTermHolidays(selectedTerm.id);
      setHolidays(res.data);
    } catch (error) {
      if (error.response?.status === 409 && error.response.data?.confirm_required) {
        setConfirmSessions(error.response.data.sessions);
        setPendingHoliday(values);
      } else {
        message.error('Tatil günü eklenemedi.');
      }
    }
  };

  const handleConfirmHoliday = () => handleAddHoliday(pendingHoliday, true);

  const handleDeleteHoliday = async (holidayId) => {
    try {
      await deleteTermHoliday(selectedTerm.id, holidayId);
      message.success('Tatil günü silindi.');
      const res = await getTermHolidays(selectedTerm.id);
      setHolidays(res.data);
    } catch (error) {
      message.error('Tatil günü silinemedi.');
    }
  };

  const handlePrices = async (term) => {
    setSelectedTerm(term);
    try {
      const res = await getTermPrices(term.id);
      setPrices(res.data);
    } catch (error) {
      message.error('Fiyatlar yüklenemedi.');
    }
    setPriceModalOpen(true);
  };

  const handleAddPrice = async (values) => {
    try {
      // valid_from bir dayjs NESNESİ. Ham gönderilirse JSON'a çevrilirken
      // toISOString() çalışıyor, Türkiye UTC+3 olduğu için yerel gece yarısı
      // bir önceki güne düşüyor ve backend'in date cast'i onu öyle kaydediyor.
      // Kayma sabit de değildi: kayıt tekrar okunup tekrar kaydedildiğinde
      // birikiyordu (2 Ocak -> 1 Ocak -> 31 Aralık -> 30 Aralık...).
      // Aynı dosyadaki dönem ve tatil formları zaten .format() yapıyor,
      // yalnızca fiyat formu bu adımı atlamıştı. Fiyat görsel bir alan değil,
      // ders ücretlendirmesi valid_from'a bakıyor.
      const payload = {
        ...values,
        valid_from: values.valid_from?.format?.('YYYY-MM-DD') ?? values.valid_from,
        ...(values.valid_until
          ? { valid_until: values.valid_until?.format?.('YYYY-MM-DD') ?? values.valid_until }
          : {}),
      };

      if (editingPrice) {
        await updateTermPrice(selectedTerm.id, editingPrice.id, payload);
        message.success('Fiyat güncellendi.');
        setEditingPrice(null);
      } else {
        await addTermPrice(selectedTerm.id, payload);
        message.success('Fiyat eklendi.');
      }
      priceForm.resetFields();
      const res = await getTermPrices(selectedTerm.id);
      setPrices(res.data);
    } catch (error) {
      message.error(apiErrorMessage(error, editingPrice ? 'Fiyat güncellenemedi.' : 'Fiyat eklenemedi.'));
    }
  };

  const handleEditPrice = (price) => {
    setEditingPrice(price);
    priceForm.setFieldsValue({
      price_per_lesson: price.price_per_lesson,
      valid_from:       dayjs(price.valid_from),
    });
  };

  const handleCancelEditPrice = () => {
    setEditingPrice(null);
    priceForm.resetFields();
  };

  const handleDeletePrice = async (priceId) => {
    try {
      await deleteTermPrice(selectedTerm.id, priceId);
      message.success('Fiyat silindi.');
      const res = await getTermPrices(selectedTerm.id);
      setPrices(res.data);
    } catch (error) {
      message.error('Fiyat silinemedi.');
    }
  };

  const columns = [
    {
      title: 'Dönem Adı',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Tür',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const types = { summer: 'Yaz', winter: 'Kış', spring: 'Bahar', fall: 'Sonbahar' };
        return <Tag>{types[type] || type}</Tag>;
      },
    },
    {
      title: 'Tarih',
      key: 'date',
      render: (_, r) => `${dayjs(r.start_date).format('DD.MM.YYYY')} — ${dayjs(r.end_date).format('DD.MM.YYYY')}`,
    },
    {
      title: 'Şube',
      key: 'branch',
      render: (_, record) => record.branch?.name || '—',
    },
    {
      title: 'Durum',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (is_active, record) => (
        <Switch
          checked={is_active}
          checkedChildren="Aktif"
          unCheckedChildren="Pasif"
          style={{ backgroundColor: is_active ? '#52c41a' : '#ff4d4f' }}
          onChange={async (checked) => {
            try {
              await updateTerm(record.id, { is_active: checked });
              message.success('Durum güncellendi.');
              fetchTerms();
            } catch (error) {
              message.error('Durum güncellenemedi.');
            }
          }}
        />
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<CalendarOutlined />} size="small" onClick={() => handleHolidays(record)}>
            Tatiller
          </Button>
          <Button icon={<DollarOutlined />} size="small" onClick={() => handlePrices(record)}>
            Fiyatlar
          </Button>
          {canGenerateSessions && (
            /* Onay penceresi yerine form: gün bazlı saat, derslik seçimi ve
               önizleme gerekiyordu. Tek aralık tüm günlere uygulanınca hafta
               içine gereksiz boş slot açılıyordu. */
            <Button
              icon={<ThunderboltOutlined />}
              size="small"
              type="dashed"
              onClick={() => setGenerateTerm(record)}
            >
              Toplu Ders
            </Button>
          )}
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Düzenle
          </Button>
          <Popconfirm
            title="Dönemi silmek istediğinize emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button icon={<DeleteOutlined />} danger size="small">Sil</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <span className="font-medium">Dönem Listesi</span>
          {!branchId && (
            <Select
              placeholder="Şube filtrele"
              allowClear
              style={{ width: 180 }}
              value={branchFilter}
              onChange={v => setBranchFilter(v ?? null)}
            >
              {branches.map(b => <Option key={b.id} value={b.id}>{b.name}</Option>)}
            </Select>
          )}
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingTerm(null);
            form.resetFields();
            setModalOpen(true);
          }}
        >
          Yeni Dönem
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={terms}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          onChange: (page) => fetchTerms(page),
          showTotal: (total) => `Toplam ${total} dönem`,
        }}
      />

      {/* Dönem Modal */}
      <Modal
        title={editingTerm ? 'Dönem Düzenle' : 'Yeni Dönem'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Dönem Adı" rules={[{ required: true }]}>
            <Input placeholder="2026 Yaz Dönemi" />
          </Form.Item>
          <Form.Item name="type" label="Tür" rules={[{ required: true }]}>
            <Select placeholder="Dönem türü seçiniz">
              <Option value="summer">Yaz</Option>
              <Option value="winter">Kış</Option>
              <Option value="spring">Bahar</Option>
              <Option value="fall">Sonbahar</Option>
            </Select>
          </Form.Item>
          <Form.Item name="date_range" label="Tarih Aralığı" rules={[{ required: true }]}>
            <RangePicker className="w-full" format="DD.MM.YYYY" />
          </Form.Item>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="first_slot_time" label="İlk Ders Saati">
              <Select placeholder="10:30" options={TIME_OPTIONS} showSearch allowClear className="w-full" />
            </Form.Item>
            <Form.Item name="last_slot_time" label="Son Ders Saati">
              <Select placeholder="14:30" options={TIME_OPTIONS} showSearch allowClear className="w-full" />
            </Form.Item>
          </div>
          {!branchId && (
            <Form.Item name="branch_id" label="Şube" rules={[{ required: true }]}>
              <BranchSelect branches={branches} />
            </Form.Item>
          )}
          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>İptal</Button>
              <Button type="primary" htmlType="submit">
                {editingTerm ? 'Güncelle' : 'Oluştur'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Tatil Günleri Modal */}
      <Modal
        title={`${selectedTerm?.name} — Tatil Günleri`}
        open={holidayModalOpen}
        onCancel={() => setHolidayModalOpen(false)}
        footer={null}
        width={600}
      >
        <Form form={holidayForm} layout="inline" onFinish={handleAddHoliday} className="mb-4">
          <Form.Item name="date" rules={[{ required: true }]}>
            <DatePicker format="DD.MM.YYYY" placeholder="Tarih seçiniz" />
          </Form.Item>
          <Form.Item name="description">
            <Input placeholder="Açıklama (opsiyonel)" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>Ekle</Button>
          </Form.Item>
        </Form>
        {holidays.map((h) => (
          <div key={h.id} className="flex justify-between items-center p-2 border rounded mb-2">
            <span>{dayjs(h.date).format('DD.MM.YYYY')} {h.description && `— ${h.description}`}</span>
            <Popconfirm
              title="Tatil günü silinsin mi?"
              onConfirm={() => handleDeleteHoliday(h.id)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button icon={<DeleteOutlined />} danger size="small" />
            </Popconfirm>
          </div>
        ))}
        {holidays.length === 0 && <p className="text-gray-400">Henüz tatil günü eklenmemiş.</p>}
      </Modal>

      {/* Tatil — Silinecek Dersler Onay Modalı */}
      <Modal
        title="Dikkat — Bu Tarihteki Dersler Silinecek"
        open={!!confirmSessions}
        onCancel={() => { setConfirmSessions(null); setPendingHoliday(null); }}
        onOk={handleConfirmHoliday}
        okText="Evet, Sil ve Tatil Ekle"
        cancelText="İptal"
        okButtonProps={{ danger: true }}
        width={520}
      >
        <p className="mb-3 text-gray-600">
          <strong>{pendingHoliday?.date?.format('DD.MM.YYYY')}</strong> tarihinde aşağıdaki dersler mevcut.
          Tatil eklenirse bu dersler ve ilgili ücret kayıtları silinecektir.
        </p>
        <div style={{ maxHeight: 280, overflowY: 'auto' }}>
          {confirmSessions?.map((s, i) => (
            <div key={i} className="flex justify-between items-center p-2 border-b last:border-0 text-sm">
              <span className="font-medium">{s.classroom}</span>
              <span className="text-gray-500">{s.start_time}</span>
              <span className="text-gray-700">{s.student_name || '—'}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-red-500 text-sm">Bu işlem geri alınamaz.</p>
      </Modal>

      {/* Fiyat Modal */}
      <Modal
        title={`${selectedTerm?.name} — Ders Fiyatları`}
        open={priceModalOpen}
        onCancel={() => { setPriceModalOpen(false); setEditingPrice(null); priceForm.resetFields(); }}
        footer={null}
        width={620}
      >
        <Form form={priceForm} layout="inline" onFinish={handleAddPrice} className="mb-4">
          <Form.Item name="price_per_lesson" rules={[{ required: true }]}>
            <Input type="number" placeholder="Ders başı fiyat (₺)" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="valid_from" rules={[{ required: true }]}>
            <DatePicker format="DD.MM.YYYY" placeholder="Başlangıç tarihi" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={editingPrice ? <EditOutlined /> : <PlusOutlined />}>
                {editingPrice ? 'Güncelle' : 'Ekle'}
              </Button>
              {editingPrice && (
                <Button onClick={handleCancelEditPrice}>İptal</Button>
              )}
            </Space>
          </Form.Item>
        </Form>
        {prices.map((p) => (
          <div
            key={p.id}
            className="flex justify-between items-center p-2 border rounded mb-2"
            style={{ borderColor: editingPrice?.id === p.id ? '#1677ff' : undefined }}
          >
            <div>
              <strong>{p.price_per_lesson} ₺/ders</strong>
              <span className="ml-2 text-gray-400 text-sm">{dayjs(p.valid_from).format('DD.MM.YYYY')} tarihinden itibaren</span>
            </div>
            <Space>
              <Button icon={<EditOutlined />} size="small" onClick={() => handleEditPrice(p)} />
              <Popconfirm
                title="Fiyat silinsin mi?"
                onConfirm={() => handleDeletePrice(p.id)}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button icon={<DeleteOutlined />} danger size="small" />
              </Popconfirm>
            </Space>
          </div>
        ))}
        {prices.length === 0 && <p className="text-gray-400">Henüz fiyat eklenmemiş.</p>}
      </Modal>

      <GenerateSessionsModal
        open={!!generateTerm}
        term={generateTerm}
        onClose={() => setGenerateTerm(null)}
        onDone={fetchTerms}
      />
    </>
  );
};

export default TermsTab;